import React, { useEffect, useState } from 'react';
import * as notificationsApi from '../../utils/notificationsApi';

const KNOWN_TYPES: Array<{ key: string; label: string; description: string }> = [
  {
    key: 'appointment_reassigned',
    label: 'Appointment reassigned',
    description: 'When an appointment is moved from one team member to another.',
  },
];

const NotificationsSettings: React.FC = () => {
  const [enabledTypes, setEnabledTypes] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const prefs = await notificationsApi.getNotificationPreferences();
        setEnabledTypes(prefs.enabled_types || {});
      } catch (e: any) {
        setError(e?.message || 'Failed to load notification preferences');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = async (key: string) => {
    setSuccess(null);
    setError(null);
    const prev = enabledTypes;
    const next = { ...prev, [key]: !(prev[key] ?? true) };
    setEnabledTypes(next);
    setSaving(true);
    try {
      const resp = await notificationsApi.updateNotificationPreferences(next);
      setEnabledTypes(resp.enabled_types || next);
      setSuccess('Saved');
      setTimeout(() => setSuccess(null), 1500);
    } catch (e: any) {
      setError(e?.message || 'Failed to save preferences');
      setEnabledTypes(prev); // revert
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-8">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary">Notifications</h1>
        <p className="text-sm text-gray-600 dark:text-dark-text-muted mt-1">
          Control which in-app notifications you receive.
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 px-4 py-3 text-sm text-green-800 dark:text-green-300">
          {success}
        </div>
      )}

      <div className="rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg-secondary overflow-hidden">
        {loading ? (
          <div className="px-4 py-6 text-sm text-gray-600 dark:text-dark-text-muted">Loading…</div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-dark-border">
            {KNOWN_TYPES.map((t) => {
              const enabled = enabledTypes[t.key] ?? true; // default enabled
              return (
                <div key={t.key} className="px-4 py-4 flex items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">{t.label}</div>
                    <div className="text-sm text-gray-600 dark:text-dark-text-muted">{t.description}</div>
                  </div>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => toggle(t.key)}
                    className={`h-9 px-3 rounded-lg text-sm font-medium border transition-colors ${
                      enabled
                        ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                        : 'bg-white dark:bg-dark-bg-secondary text-gray-700 dark:text-dark-text-secondary border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
                    } ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    {enabled ? 'On' : 'Off'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsSettings;


